import React from "react";
import { ProductImageCard } from "components/shared/Card/ProductImageCard";
import {
  GrnItemCardWrapper,
  ProductThumb,
  DisputeRowGrid,
  DisputeDescriptionWrapper,
  DisputeDescriptionTextarea,
  GrnInfoLabelWrapper,
} from "./styles.grn";
import InfoBanner from "components/shared/Label/InfoLabel";
import { GRN_DISPUTE_ISSUE_TYPE } from "utils/constants/others";
import { strings } from "utils/constants/strings";
import Input from "components/shared/InputTypes/Input/Input";

/** Price-dispute notice, per-item claim cards and the description box. Used by the mobile page and the desktop drawer. */
const GrnDisputeItems = ({
  showPriceDisputeNotAllowedBanner,
  disputeableItems,
  disputeAllowedMap,
  disputeItemIssues,
  handleClaimChange,
  itemErrors,
  disputeIssueDescription,
  setDisputeIssueDescription,
  descriptionError,
  setDescriptionError,
}) => {


  return (
  <>
    {/* Price dispute info banner */}
    {showPriceDisputeNotAllowedBanner && (
      <GrnInfoLabelWrapper>
        <InfoBanner
          $bg="var(--color-base-orange-100, rgba(255, 241, 230, 1))"
          $fontSize="12px"
          $lineHeight="14px"
        >
          {strings.priceDisputeNotAllowed}
        </InfoBanner>
      </GrnInfoLabelWrapper>
    )}

    {/* Item-wise dispute input cards */}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        padding: "16px 16px 16px 16px",
      }}
    >
      {disputeableItems.map((item) => {
        const allowed = disputeAllowedMap[item.id];
        const current = disputeItemIssues[item.id] || {};

        const footerDiv =
          itemErrors[item.id] ? (
            <InfoBanner
              $color="var(--color-base-red-500, rgba(211, 47, 47, 1))"
              $bg="transparent"
              $border="none"
              iconStyle={{ filter: "invert(27%) sepia(100%) saturate(5000%) hue-rotate(340deg) brightness(80%)" }}
            >{itemErrors[item.id]}</InfoBanner>
          ) : null

        const innerDiv = (
          <>
            {/* GRN quantity dispute row */}
            {allowed.allowed_issue_types.includes(
              GRN_DISPUTE_ISSUE_TYPE.GRN_QUANTITY
            ) && (
                <DisputeRowGrid>
                    <Input
                      label={strings.grnDisputeGrnQuantity}
                      suffix={item.buy_measurement_unit}
                      value={String(item.grn_quantity ?? "")}
                      disabled
                      fullWidth
                      borderRadius="12px"
                    />
                    <Input
                      label={strings.grnDisputeYourClaim}
                      type="number"
                      placeholder="0"
                      value={current.claimedGrnQty || ""}
                      onChange={(value) =>
                        handleClaimChange(
                          item.id,
                          "claimedGrnQty"
                        )(value)
                      }
                      onWheel={(e) => e.target.blur()}
                      min="0"
                      step="0.01"
                      suffix={item.buy_measurement_unit}
                      fullWidth
                      borderRadius="12px"
                    />
                </DisputeRowGrid>
              )}

            {/* PO GST % dispute row */}
            {allowed.allowed_issue_types.includes(
              GRN_DISPUTE_ISSUE_TYPE.GST_PERCENTAGE
            ) && (
                <DisputeRowGrid>
                    <Input
                      label={strings.grnDisputePoGstPercent}
                      suffix="%"
                      value={String(item.gst_rate ?? "")}
                      disabled
                      fullWidth
                      borderRadius="12px"
                    />
                    <Input
                      label={strings.grnDisputeYourClaim}
                      type="number"
                      placeholder="0"
                      value={current.claimedGstRate || ""}
                      onChange={(value) =>
                        handleClaimChange(
                          item.id,
                          "claimedGstRate"
                        )(value)
                      }
                      onWheel={(e) => e.target.blur()}
                      min="0"
                      step="0.01"
                      suffix="%"
                      fullWidth
                      borderRadius="12px"
                    />
                </DisputeRowGrid>
              )}

            {/* GRN price per unit dispute row */}
            {allowed.allowed_issue_types.includes(
              GRN_DISPUTE_ISSUE_TYPE.PRICE_PER_UNIT
            ) && (
                <DisputeRowGrid>
                    <Input
                      label={strings.grnDisputeGrnPricePerUnit}
                      suffix={`₹/${item?.buy_measurement_unit}`}
                      value={String(item.grn_price_per_unit ?? "")}
                      disabled
                      fullWidth
                      borderRadius="12px"
                    />
                    <Input
                      label={strings.grnDisputeYourClaim}
                      type="number"
                      placeholder="0"
                      value={current.claimedGrnPrice || ""}
                      onChange={(value) =>
                        handleClaimChange(
                          item.id,
                          "claimedGrnPrice"
                        )(value)
                      }
                      onWheel={(e) => e.target.blur()}
                      min="0"
                      step="0.01"
                      suffix={`₹/${item?.buy_measurement_unit}`}
                      fullWidth
                      borderRadius="12px"
                    />
                </DisputeRowGrid>
              )}
          </>
        )

        return (
          <GrnItemCardWrapper key={item?.id}>
            {/* Product header */}
            <ProductImageCard
              imageComponent={<ProductThumb src={item?.product_image} alt={item?.product_name} />}
              title={item?.product_name}
              subtitle={`\u20B9${item?.po_item_price_per_unit}/${item?.buy_measurement_unit}`}
              footerDiv={footerDiv}
              innerDiv={innerDiv}

            />
          </GrnItemCardWrapper>
        );
      })}
    </div>

    <div style={{ padding: "0 16px 16px 16px" }}>
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e6e9ef",
          borderRadius: "16px",
          padding: "16px",
          boxShadow: "0px 1px 4px rgba(28, 28, 28, 0.06)",
        }}
      >
        <DisputeDescriptionWrapper style={{ marginTop: 0 }}>
          <span className="fs-16 fw-700 text-dark">
            {strings.grnDisputeShareDetails}
          </span>
          <DisputeDescriptionTextarea
            placeholder={strings.grnDisputeDescriptionPlaceholder}
            value={disputeIssueDescription}
            onChange={(e) => {
              setDisputeIssueDescription(e.target.value);
              if (descriptionError) setDescriptionError("");
            }}
            rows={5}
          />
        </DisputeDescriptionWrapper>
      </div>
      {descriptionError && (
        <InfoBanner
          $color="var(--color-base-red-500, rgba(211, 47, 47, 1))"
          $bg="transparent"
          $border="none"
          iconStyle={{ filter: "invert(27%) sepia(100%) saturate(5000%) hue-rotate(340deg) brightness(80%)" }}
        >{descriptionError}</InfoBanner>
      )}
    </div>
  </>
)};

export default GrnDisputeItems;
